import clsx from 'clsx';
import { forwardRef, HTMLProps, MouseEventHandler, PropsWithChildren, useMemo } from 'react';
import { Link } from 'react-router';
import { useUniqueId } from '../../hooks/use-unique-id/index.ts';
import { ThemeCardTokenValues } from '../../theme/index.ts';
import { tokenOverwrite } from '../../theme/utils/token-overwrite/token-overwrite.ts';
import { CardProvider } from './card-context.ts';
import classes from './card.module.scss';

export type CardProps = PropsWithChildren &
  Omit<HTMLProps<HTMLDivElement>, 'onClick' | 'ref'> & {
    /**
     * Add custom click handler
     */
    onClick?: MouseEventHandler;

    /**
   * A string representing the URL or hyperlink reference.
   * It is optional and can be undefined.
   * Typically used for defining the target destination of a link or reference.
   */
    href?: string;

    /**
     * Local theme token overwrite
     */
    tokens?: Partial<Record<ThemeCardTokenValues, string | number>>;
  };

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ children, onClick, href, tokens = {}, ...props }, ref) => {
    const labelId = useUniqueId('card-label-');
    const themeTokens = useMemo(() => tokenOverwrite('card', tokens), [tokens]);

    return (
      <CardProvider value={{ labelId }}>
        <article
          {...props}
          ref={ref}
          style={{ ...props.style, ...themeTokens }}
          className={clsx(classes.root, 'card-component', props.className, {
            [classes.clickable]: !!onClick || !!href
          })}
        >
          {href && <Link to={href} aria-labelledby={labelId}>
            <span className={classes.link} />
          </Link>}

          {onClick && <button onClick={onClick} aria-labelledby={labelId} className={classes.button}>
            <span className={classes.link}></span>
          </button>}

          {children}
        </article>
      </CardProvider>
    );
  }
);

Card.displayName = 'Card';